import {
  STUDENT_BILLING_EVENT_BILLING_MONTH_RULES,
  STUDENT_BILLING_EVENT_FEE_ITEM_STATUSES,
  STUDENT_BILLING_FEE_STATUSES,
  STUDENT_BILLING_LESSON_KINDS,
  STUDENT_BILLING_MANUAL_TARGET_TYPES,
  STUDENT_BILLING_MIGRATION_COMPARISON_STATUSES,
  expectArray,
  expectBoolean,
  expectRecord,
  expectString,
  parseCreateStudentBillingEventFeeItemRequest,
  parseCreateStudentBillingManualSourceRequest,
  parsePreviewStudentBillingManualSourceRequest,
  parsePutStudentBillingLessonFeeRequest,
  parseStudentBillingCurrency,
  parseStudentBillingDate,
  parseStudentBillingIdentifier,
  parseStudentBillingManualBatch,
  parseStudentBillingManualReason,
  parseStudentBillingServiceMonth,
  parseStudentBillingSignedNonZeroAmount,
  parseStudentBillingTargetFingerprint,
  parseUpdateStudentBillingEventFeeItemRequest,
  type CreateStudentBillingEventFeeItemRequest,
  type CreateStudentBillingManualSourceRequest,
  type PreviewStudentBillingManualSourceRequest,
  type PutStudentBillingLessonFeeRequest,
  type StudentBillingEventFeeDetailDto,
  type StudentBillingEventFeeItemDto,
  type StudentBillingLessonFeeDetailDto,
  type StudentBillingLessonFeeDto,
  type StudentBillingLessonMigrationComparisonDto,
  type StudentBillingManualBatchDetailDto,
  type StudentBillingManualBatchSummaryDto,
  type StudentBillingManualPreviewDto,
  type StudentBillingManualTargetDto,
  type StudentBillingManualTargetType,
  type UpdateStudentBillingEventFeeItemRequest
} from "@madamuh/domain";
import { apiGet, apiPatchWithHeaders, apiPostWithHeaders, apiPutWithHeaders } from "./client";

export type StudentBillingFeeMasterCapabilities = {
  canReadFeeMasters: boolean;
  canManageFeeMasters: boolean;
};

export type StudentBillingManualSourceCapabilities = {
  canReadManualSources: boolean;
  canManageManualSources: boolean;
};

export type StudentBillingLessonFeeListResponse = {
  capabilities: StudentBillingFeeMasterCapabilities;
  items: StudentBillingLessonFeeDetailDto[];
};

export type StudentBillingLessonFeeResponse = StudentBillingLessonFeeDetailDto & {
  capabilities: StudentBillingFeeMasterCapabilities;
};

export type StudentBillingEventFeeListResponse = {
  capabilities: StudentBillingFeeMasterCapabilities;
  items: StudentBillingEventFeeDetailDto[];
};

export type StudentBillingEventFeeResponse = StudentBillingEventFeeDetailDto & {
  capabilities: StudentBillingFeeMasterCapabilities;
};

export type StudentBillingManualListResponse = {
  capabilities: StudentBillingManualSourceCapabilities;
  serviceMonth: string;
  targetType: StudentBillingManualTargetType | null;
  items: StudentBillingManualBatchSummaryDto[];
};

function exact(record: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
  if (unknown.length > 0) throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}

function enumValue<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  const parsed = expectString(value, path);
  if (!(allowed as readonly string[]).includes(parsed)) throw new Error(`${path} is invalid`);
  return parsed as T;
}

function safeInteger(value: unknown, path: string, minimum = 0): number {
  if (!Number.isSafeInteger(value) || Number(value) < minimum) throw new Error(`${path} is invalid`);
  return Number(value);
}

function iso(value: unknown, path: string): string {
  const parsed = expectString(value, path);
  if (!Number.isFinite(new Date(parsed).getTime())) throw new Error(`${path} is invalid`);
  return new Date(parsed).toISOString();
}

function nullableDate(value: unknown, path: string): string | null {
  return value === null ? null : parseStudentBillingDate(value, path);
}

function nullableIdentifier(value: unknown, path: string): string | null {
  return value === null ? null : parseStudentBillingIdentifier(value, path);
}

function feeCapabilities(value: unknown, path: string): StudentBillingFeeMasterCapabilities {
  const record = expectRecord(value, path);
  exact(record, ["canReadFeeMasters", "canManageFeeMasters"], path);
  return {
    canReadFeeMasters: expectBoolean(record.canReadFeeMasters, `${path}.canReadFeeMasters`),
    canManageFeeMasters: expectBoolean(record.canManageFeeMasters, `${path}.canManageFeeMasters`)
  };
}

function manualCapabilities(value: unknown, path: string): StudentBillingManualSourceCapabilities {
  const record = expectRecord(value, path);
  exact(record, ["canReadManualSources", "canManageManualSources"], path);
  return {
    canReadManualSources: expectBoolean(record.canReadManualSources, `${path}.canReadManualSources`),
    canManageManualSources: expectBoolean(record.canManageManualSources, `${path}.canManageManualSources`)
  };
}

function lessonFee(value: unknown, path: string): StudentBillingLessonFeeDto {
  const record = expectRecord(value, path);
  exact(record, [
    "settingId", "lessonId", "lessonTitle", "lessonKind", "amount", "currency", "effectiveFrom",
    "effectiveTo", "status", "version", "firstBillableServiceMonthPreview", "createdAt", "updatedAt"
  ], path);
  const firstBillableServiceMonthPreview = record.firstBillableServiceMonthPreview === null
    ? null
    : parseStudentBillingServiceMonth(record.firstBillableServiceMonthPreview, `${path}.firstBillableServiceMonthPreview`);
  return {
    settingId: parseStudentBillingIdentifier(record.settingId, `${path}.settingId`),
    lessonId: parseStudentBillingIdentifier(record.lessonId, `${path}.lessonId`),
    lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
    lessonKind: enumValue(record.lessonKind, STUDENT_BILLING_LESSON_KINDS, `${path}.lessonKind`),
    amount: safeInteger(record.amount, `${path}.amount`),
    currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
    effectiveFrom: parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`),
    effectiveTo: nullableDate(record.effectiveTo, `${path}.effectiveTo`),
    status: enumValue(record.status, STUDENT_BILLING_FEE_STATUSES, `${path}.status`),
    version: safeInteger(record.version, `${path}.version`, 1),
    firstBillableServiceMonthPreview,
    createdAt: iso(record.createdAt, `${path}.createdAt`),
    updatedAt: iso(record.updatedAt, `${path}.updatedAt`)
  };
}

function lessonDetail(value: unknown, path: string): StudentBillingLessonFeeDetailDto {
  const record = expectRecord(value, path);
  exact(record, ["lessonId", "lessonTitle", "lessonKind", "configured", "current", "history"], path);
  const current = record.current === null ? null : lessonFee(record.current, `${path}.current`);
  return {
    lessonId: parseStudentBillingIdentifier(record.lessonId, `${path}.lessonId`),
    lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
    lessonKind: enumValue(record.lessonKind, STUDENT_BILLING_LESSON_KINDS, `${path}.lessonKind`),
    configured: expectBoolean(record.configured, `${path}.configured`),
    current,
    history: expectArray(record.history, lessonFee, `${path}.history`)
  };
}

function eventItem(value: unknown, path: string): StudentBillingEventFeeItemDto {
  const record = expectRecord(value, path);
  exact(record, [
    "feeItemId", "label", "amount", "currency", "billingMonthRule", "explicitServiceMonth", "status",
    "version", "createdAt", "updatedAt", "resolvedServiceMonthPreview"
  ], path);
  const billingMonthRule = enumValue(record.billingMonthRule, STUDENT_BILLING_EVENT_BILLING_MONTH_RULES, `${path}.billingMonthRule`);
  const explicitServiceMonth = record.explicitServiceMonth === null
    ? null
    : parseStudentBillingServiceMonth(record.explicitServiceMonth, `${path}.explicitServiceMonth`);
  if ((billingMonthRule === "eventFirstMonth") !== (explicitServiceMonth === null)) throw new Error(`${path} month rule is invalid`);
  return {
    feeItemId: parseStudentBillingIdentifier(record.feeItemId, `${path}.feeItemId`),
    label: expectString(record.label, `${path}.label`),
    amount: safeInteger(record.amount, `${path}.amount`),
    currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
    billingMonthRule,
    explicitServiceMonth,
    status: enumValue(record.status, STUDENT_BILLING_EVENT_FEE_ITEM_STATUSES, `${path}.status`),
    version: safeInteger(record.version, `${path}.version`, 1),
    createdAt: iso(record.createdAt, `${path}.createdAt`),
    updatedAt: iso(record.updatedAt, `${path}.updatedAt`),
    resolvedServiceMonthPreview: parseStudentBillingServiceMonth(record.resolvedServiceMonthPreview, `${path}.resolvedServiceMonthPreview`)
  };
}

function eventDetail(value: unknown, path: string): StudentBillingEventFeeDetailDto {
  const record = expectRecord(value, path);
  exact(record, ["eventId", "eventTitle", "eventStartAt", "feeConfigured", "chargeTargetReady", "version", "items"], path);
  if (record.chargeTargetReady !== "notImplemented") throw new Error(`${path}.chargeTargetReady is invalid`);
  return {
    eventId: parseStudentBillingIdentifier(record.eventId, `${path}.eventId`),
    eventTitle: expectString(record.eventTitle, `${path}.eventTitle`),
    eventStartAt: iso(record.eventStartAt, `${path}.eventStartAt`),
    feeConfigured: expectBoolean(record.feeConfigured, `${path}.feeConfigured`),
    chargeTargetReady: "notImplemented",
    version: record.version === null ? null : safeInteger(record.version, `${path}.version`, 1),
    items: expectArray(record.items, eventItem, `${path}.items`)
  };
}

function manualTarget(value: unknown, path: string): StudentBillingManualTargetDto {
  const record = expectRecord(value, path);
  exact(record, ["studentId", "displayName"], path);
  return {
    studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
    displayName: expectString(record.displayName, `${path}.displayName`)
  };
}

function manualPreview(value: unknown, path: string): StudentBillingManualPreviewDto {
  const record = expectRecord(value, path);
  exact(record, [
    "targetType", "sourceStudentId", "sourceLessonId", "sourceLessonTitle", "serviceMonth", "amountPerStudent",
    "currency", "reason", "targetCount", "targets", "targetFingerprint", "totalAmountPreview"
  ], path);
  const targetType = enumValue(record.targetType, STUDENT_BILLING_MANUAL_TARGET_TYPES, `${path}.targetType`);
  const sourceStudentId = nullableIdentifier(record.sourceStudentId, `${path}.sourceStudentId`);
  const sourceLessonId = nullableIdentifier(record.sourceLessonId, `${path}.sourceLessonId`);
  const sourceLessonTitle = record.sourceLessonTitle === null ? null : expectString(record.sourceLessonTitle, `${path}.sourceLessonTitle`);
  if (targetType === "student" ? sourceStudentId === null || sourceLessonId !== null : sourceStudentId !== null || sourceLessonId === null) {
    throw new Error(`${path} target binding is invalid`);
  }
  const targets = expectArray(record.targets, manualTarget, `${path}.targets`);
  const targetCount = safeInteger(record.targetCount, `${path}.targetCount`, 1);
  if (targets.length !== targetCount) throw new Error(`${path}.targetCount does not match targets`);
  return {
    targetType,
    sourceStudentId,
    sourceLessonId,
    sourceLessonTitle,
    serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
    amountPerStudent: parseStudentBillingSignedNonZeroAmount(record.amountPerStudent, `${path}.amountPerStudent`),
    currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
    reason: parseStudentBillingManualReason(record.reason, `${path}.reason`),
    targetCount,
    targets,
    targetFingerprint: parseStudentBillingTargetFingerprint(record.targetFingerprint, `${path}.targetFingerprint`),
    totalAmountPreview: parseStudentBillingSignedNonZeroAmount(record.totalAmountPreview, `${path}.totalAmountPreview`)
  };
}

const manualBatchFields = [
  "batchId", "tenantId", "targetType", "sourceStudentId", "sourceLessonId", "sourceLessonTitle", "serviceMonth",
  "amountPerStudent", "currency", "reason", "targetCount", "targetFingerprint", "status", "createdByPlatformUserId",
  "createdAt", "updatedAt"
] as const;

function manualBatchSummary(value: unknown, path: string): StudentBillingManualBatchSummaryDto {
  const record = expectRecord(value, path);
  exact(record, [...manualBatchFields, "totalAmount"], path);
  const base = Object.fromEntries(manualBatchFields.map((key) => [key, record[key]]));
  return {
    ...parseStudentBillingManualBatch(base, path),
    totalAmount: parseStudentBillingSignedNonZeroAmount(record.totalAmount, `${path}.totalAmount`)
  };
}

function manualBatchDetail(value: unknown, path: string): StudentBillingManualBatchDetailDto {
  const record = expectRecord(value, path);
  exact(record, [...manualBatchFields, "totalAmount", "targets"], path);
  const summaryRecord = Object.fromEntries([...manualBatchFields, "totalAmount"].map((key) => [key, record[key]]));
  const summary = manualBatchSummary(summaryRecord, path);
  const targets = expectArray(record.targets, manualTarget, `${path}.targets`);
  if (targets.length !== summary.targetCount) throw new Error(`${path}.targets does not match targetCount`);
  return { ...summary, targets };
}

function mutationHeaders(): Record<string, string> {
  return { "Idempotency-Key": crypto.randomUUID() };
}

function root(tenantSlug: string): string {
  return `/api/v1/admin/tenants/${encodeURIComponent(tenantSlug)}/billing`;
}

export async function fetchStudentBillingLessonFees(tenantSlug: string): Promise<StudentBillingLessonFeeListResponse> {
  const record = expectRecord(await apiGet<unknown>(`${root(tenantSlug)}/lesson-fees`), "lessonFeeList");
  exact(record, ["capabilities", "items"], "lessonFeeList");
  return { capabilities: feeCapabilities(record.capabilities, "lessonFeeList.capabilities"), items: expectArray(record.items, lessonDetail, "lessonFeeList.items") };
}

export async function fetchStudentBillingLessonFee(tenantSlug: string, lessonId: string): Promise<StudentBillingLessonFeeResponse> {
  const record = expectRecord(await apiGet<unknown>(`${root(tenantSlug)}/lesson-fees/${encodeURIComponent(lessonId)}`), "lessonFeeDetail");
  exact(record, ["capabilities", "lessonId", "lessonTitle", "lessonKind", "configured", "current", "history"], "lessonFeeDetail");
  const detailRecord = Object.fromEntries(["lessonId", "lessonTitle", "lessonKind", "configured", "current", "history"].map((key) => [key, record[key]]));
  return { capabilities: feeCapabilities(record.capabilities, "lessonFeeDetail.capabilities"), ...lessonDetail(detailRecord, "lessonFeeDetail") };
}

export async function fetchStudentBillingLessonMigration(
  tenantSlug: string,
  lessonId: string
): Promise<{ capabilities: StudentBillingFeeMasterCapabilities; lessonId: string; comparison: StudentBillingLessonMigrationComparisonDto }> {
  const path = `${root(tenantSlug)}/lesson-fees/${encodeURIComponent(lessonId)}/migration-comparison`;
  const record = expectRecord(await apiGet<unknown>(path), "lessonFeeMigration");
  exact(record, ["capabilities", "lessonId", "comparison"], "lessonFeeMigration");
  const comparison = expectRecord(record.comparison, "lessonFeeMigration.comparison");
  exact(comparison, ["status", "enrolledStudentCount", "configuredStudentCount", "distinctAmounts", "suggestedLessonAmount"], "lessonFeeMigration.comparison");
  const distinctAmounts = expectArray(comparison.distinctAmounts, (item, itemPath) => {
    const entry = expectRecord(item, itemPath);
    exact(entry, ["amount", "studentCount"], itemPath);
    return { amount: safeInteger(entry.amount, `${itemPath}.amount`), studentCount: safeInteger(entry.studentCount, `${itemPath}.studentCount`, 1) };
  }, "lessonFeeMigration.comparison.distinctAmounts");
  return {
    capabilities: feeCapabilities(record.capabilities, "lessonFeeMigration.capabilities"),
    lessonId: parseStudentBillingIdentifier(record.lessonId, "lessonFeeMigration.lessonId"),
    comparison: {
      status: enumValue(comparison.status, STUDENT_BILLING_MIGRATION_COMPARISON_STATUSES, "lessonFeeMigration.comparison.status"),
      enrolledStudentCount: safeInteger(comparison.enrolledStudentCount, "lessonFeeMigration.comparison.enrolledStudentCount"),
      configuredStudentCount: safeInteger(comparison.configuredStudentCount, "lessonFeeMigration.comparison.configuredStudentCount"),
      distinctAmounts,
      suggestedLessonAmount: comparison.suggestedLessonAmount === null ? null : safeInteger(comparison.suggestedLessonAmount, "lessonFeeMigration.comparison.suggestedLessonAmount")
    }
  };
}

export async function putStudentBillingLessonFee(
  tenantSlug: string,
  lessonId: string,
  request: PutStudentBillingLessonFeeRequest
): Promise<StudentBillingLessonFeeDto> {
  const parsed = parsePutStudentBillingLessonFeeRequest(request);
  const response = expectRecord(await apiPutWithHeaders(`${root(tenantSlug)}/lesson-fees/${encodeURIComponent(lessonId)}`, parsed, mutationHeaders()), "lessonFeeMutation");
  exact(response, ["replayed", "setting"], "lessonFeeMutation");
  expectBoolean(response.replayed, "lessonFeeMutation.replayed");
  return lessonFee(response.setting, "lessonFeeMutation.setting");
}

export async function fetchStudentBillingEventFees(tenantSlug: string): Promise<StudentBillingEventFeeListResponse> {
  const record = expectRecord(await apiGet<unknown>(`${root(tenantSlug)}/event-fees`), "eventFeeList");
  exact(record, ["capabilities", "items"], "eventFeeList");
  return { capabilities: feeCapabilities(record.capabilities, "eventFeeList.capabilities"), items: expectArray(record.items, eventDetail, "eventFeeList.items") };
}

export async function fetchStudentBillingEventFee(tenantSlug: string, eventId: string): Promise<StudentBillingEventFeeResponse> {
  const record = expectRecord(await apiGet<unknown>(`${root(tenantSlug)}/event-fees/${encodeURIComponent(eventId)}`), "eventFeeDetail");
  exact(record, ["capabilities", "eventId", "eventTitle", "eventStartAt", "feeConfigured", "chargeTargetReady", "version", "items"], "eventFeeDetail");
  const detailRecord = Object.fromEntries(["eventId", "eventTitle", "eventStartAt", "feeConfigured", "chargeTargetReady", "version", "items"].map((key) => [key, record[key]]));
  return { capabilities: feeCapabilities(record.capabilities, "eventFeeDetail.capabilities"), ...eventDetail(detailRecord, "eventFeeDetail") };
}

export async function createStudentBillingEventFeeItem(
  tenantSlug: string,
  eventId: string,
  request: CreateStudentBillingEventFeeItemRequest
): Promise<StudentBillingEventFeeDetailDto> {
  const parsed = parseCreateStudentBillingEventFeeItemRequest(request);
  const response = expectRecord(await apiPostWithHeaders(`${root(tenantSlug)}/event-fees/${encodeURIComponent(eventId)}/items`, parsed, mutationHeaders()), "eventFeeMutation");
  exact(response, ["replayed", "eventFee"], "eventFeeMutation");
  expectBoolean(response.replayed, "eventFeeMutation.replayed");
  return eventDetail(response.eventFee, "eventFeeMutation.eventFee");
}

export async function updateStudentBillingEventFeeItem(
  tenantSlug: string,
  eventId: string,
  feeItemId: string,
  request: UpdateStudentBillingEventFeeItemRequest
): Promise<StudentBillingEventFeeDetailDto> {
  const parsed = parseUpdateStudentBillingEventFeeItemRequest(request);
  const response = expectRecord(await apiPatchWithHeaders(`${root(tenantSlug)}/event-fees/${encodeURIComponent(eventId)}/items/${encodeURIComponent(feeItemId)}`, parsed, mutationHeaders()), "eventFeeMutation");
  exact(response, ["replayed", "eventFee"], "eventFeeMutation");
  expectBoolean(response.replayed, "eventFeeMutation.replayed");
  return eventDetail(response.eventFee, "eventFeeMutation.eventFee");
}

export async function previewStudentBillingManualSource(
  tenantSlug: string,
  request: PreviewStudentBillingManualSourceRequest
): Promise<{ capabilities: StudentBillingManualSourceCapabilities; preview: StudentBillingManualPreviewDto }> {
  const parsed = parsePreviewStudentBillingManualSourceRequest(request);
  const response = expectRecord(await apiPostWithHeaders(`${root(tenantSlug)}/manual-items/preview`, parsed, mutationHeaders()), "manualPreviewResponse");
  exact(response, ["capabilities", "preview"], "manualPreviewResponse");
  return { capabilities: manualCapabilities(response.capabilities, "manualPreviewResponse.capabilities"), preview: manualPreview(response.preview, "manualPreviewResponse.preview") };
}

export async function createStudentBillingManualSource(
  tenantSlug: string,
  request: CreateStudentBillingManualSourceRequest
): Promise<StudentBillingManualBatchDetailDto> {
  const parsed = parseCreateStudentBillingManualSourceRequest(request);
  const response = expectRecord(await apiPostWithHeaders(`${root(tenantSlug)}/manual-items`, parsed, mutationHeaders()), "manualCreateResponse");
  exact(response, ["replayed", "batch"], "manualCreateResponse");
  expectBoolean(response.replayed, "manualCreateResponse.replayed");
  return manualBatchDetail(response.batch, "manualCreateResponse.batch");
}

export async function fetchStudentBillingManualSources(
  tenantSlug: string,
  serviceMonth: string,
  targetType: StudentBillingManualTargetType | null
): Promise<StudentBillingManualListResponse> {
  const month = parseStudentBillingServiceMonth(serviceMonth, "serviceMonth");
  const params = new URLSearchParams({ serviceMonth: month });
  if (targetType !== null) params.set("targetType", enumValue(targetType, STUDENT_BILLING_MANUAL_TARGET_TYPES, "targetType"));
  const response = expectRecord(await apiGet<unknown>(`${root(tenantSlug)}/manual-items?${params.toString()}`), "manualListResponse");
  exact(response, ["capabilities", "serviceMonth", "targetType", "items"], "manualListResponse");
  const parsedTargetType = response.targetType === null ? null : enumValue(response.targetType, STUDENT_BILLING_MANUAL_TARGET_TYPES, "manualListResponse.targetType");
  return {
    capabilities: manualCapabilities(response.capabilities, "manualListResponse.capabilities"),
    serviceMonth: parseStudentBillingServiceMonth(response.serviceMonth, "manualListResponse.serviceMonth"),
    targetType: parsedTargetType,
    items: expectArray(response.items, manualBatchSummary, "manualListResponse.items")
  };
}

export async function fetchStudentBillingManualSource(
  tenantSlug: string,
  batchId: string
): Promise<{ capabilities: StudentBillingManualSourceCapabilities; batch: StudentBillingManualBatchDetailDto }> {
  const response = expectRecord(await apiGet<unknown>(`${root(tenantSlug)}/manual-items/${encodeURIComponent(batchId)}`), "manualDetailResponse");
  exact(response, ["capabilities", "batch"], "manualDetailResponse");
  return {
    capabilities: manualCapabilities(response.capabilities, "manualDetailResponse.capabilities"),
    batch: manualBatchDetail(response.batch, "manualDetailResponse.batch")
  };
}
